/**
 * Cookie Cleanup Plugin
 * 
 * This plugin helps prevent HTTP header size errors by:
 * 1. Cleaning up old/duplicate cookies
 * 2. Removing unnecessary Laravel session cookies
 * 3. Keeping only essential cookies
 */

export default ({ app, $config }) => {
    if (process.client) {
        cleanupCookies($config)
    }
}

/**
 * Clean up unnecessary cookies to prevent header size issues
 */
function cleanupCookies(config) {
    try {
        const cookies = document.cookie.split(';')
        const cookieNames = new Set()
        const duplicates = []
        
        // Find duplicate cookies
        cookies.forEach(cookie => {
            const name = cookie.trim().split('=')[0]
            if (cookieNames.has(name)) {
                duplicates.push(name)
            }
            cookieNames.add(name)
        })
        
        // Remove duplicate cookies
        duplicates.forEach(name => {
            deleteCookie(name, config)
        })
        
        // Remove ALL Laravel session cookies except the current one
        // These accumulate over time and cause "HTTP header too large" errors
        const currentSessionCookie = 'aproposjobs_session' // Keep only this one
        const laravelSessionPattern = /^[a-zA-Z0-9]{40}$/ // 40 char random Laravel session names
        
        const laravelSessionCookies = cookies
            .map(c => c.trim().split('=')[0])
            .filter(name => {
                // Remove if it matches Laravel's random session cookie pattern
                return laravelSessionPattern.test(name) && name !== currentSessionCookie
            })
        
        // Log what we're removing (helps debug)
        if (laravelSessionCookies.length > 0) {
            console.warn(`Removing ${laravelSessionCookies.length} old Laravel session cookies to prevent header size errors`)
        }
        
        laravelSessionCookies.forEach(name => {
            deleteCookie(name, config)
        })
        
        // Remove old Laravel session cookies (if not needed)
        const unnecessaryCookies = [
            'XSRF-TOKEN', // Not needed for Bearer token auth
            'laravel_session', // Not needed for SPA with Bearer tokens
            'apropos_jobs_session', // Old session cookie
        ]
        
        // Remove Intercom cookies (not used in codebase, likely from browser extension or third-party)
        const intercomCookies = cookies
            .map(c => c.trim().split('=')[0])
            .filter(name => name.startsWith('intercom-') || name.includes('intercom'))
        
        intercomCookies.forEach(name => {
            deleteCookie(name, config)
        })
        
        unnecessaryCookies.forEach(name => {
            if (getCookie(name)) {
                deleteCookie(name, config)
            }
        })
        
        // Log cleanup (only in debug mode)
        if (config.debug) {
            console.log('Cookie cleanup completed', {
                totalCookies: cookieNames.size,
                duplicatesRemoved: duplicates.length,
                unnecessaryRemoved: unnecessaryCookies.filter(name => getCookie(name)).length
            })
        }
    } catch (error) {
        console.warn('Cookie cleanup failed:', error)
    }
}

/**
 * Get cookie value by name
 */
function getCookie(name) {
    const nameEQ = name + "="
    const ca = document.cookie.split(';')
    
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i]
        while (c.charAt(0) === ' ') c = c.substring(1, c.length)
        if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length, c.length))
    }
    return null
}

/**
 * Delete a cookie by name
 */
function deleteCookie(name, config = {}) {
    const domain = config?.cookieDomain
    const paths = ['/', '/api', '/auth'] // Try multiple paths
    
    paths.forEach(path => {
        // Delete without domain
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}`
        
        // Delete with domain
        if (domain) {
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${domain}`
        }
        
        // Delete with parent domain (without leading dot)
        if (domain && domain.startsWith('.')) {
            const parentDomain = domain.substring(1)
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${parentDomain}`
        }
    })
}

export { cleanupCookies, getCookie, deleteCookie }
